import { type FC, type ReactElement, useEffect, useState } from 'react';
import type { CompetencyResponse, StudentMissionResponse } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import type { CompetenciesMapType, ListFilter, Options } from '@/components/student-missions/student-mission.types.ts';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/tr';
import Pagination from '../../pagination';
import { useSizes } from '@/utility/responsive.ts';
import Select from 'react-select';

dayjs.extend(relativeTime);
dayjs.locale('tr');

type Props = {
	list?: StudentMissionResponse[];
	competenciesList?: CompetencyResponse[];
	onChangeFilter?: (filters: ListFilter) => void;
	onDetail?: (studentMissionId: number) => void;
	onCreateNewMission?: () => void;
	onPageChange?: (page: number) => void;
	currentPage?: number;
	perPageSize?: number;
	totalPages?: number;
};

const MissionStatusList = [
	{ value: 'TODO,IN_PROGRESS,DONE', label: 'Tümü' },
	{ value: 'TODO', label: 'Başlanmadı' },
	{ value: 'IN_PROGRESS', label: 'Devam Ediyor' },
	{ value: 'DONE', label: 'Tamamlandı' }
];

export const MissionTable: FC<Props> = ({ list, competenciesList, onChangeFilter, totalPages, currentPage, onCreateNewMission, onPageChange, onDetail, perPageSize }: Props): ReactElement<Props> => {
	const { isMobile } = useSizes();

	const [showOtherExpert, setShowOtherExpert] = useState<boolean>(false);
	const [competenciesMap, setCompetenciesMap] = useState<CompetenciesMapType | null>(null);
	const [competencies, setCompetencies] = useState<Options[]>([]);

	const [selectedCompetencies, setSelectedCompetencies] = useState<Options | null>(null);
	const [selectedStatus, setSelectedStatus] = useState<Options | null>(null);
	const [sort, setSort] = useState<string>('');

	const handleFilter = (status: any, showOtherExpert: boolean, sort: string[] = []): void => {
		const statuses: any[] = status?.split(',') || ['TODO', 'IN_PROGRESS', 'DONE'];

		onChangeFilter?.({
			showOtherExpert,
			statuses,
			page: 0,
			size: 10,
			sort
		});
	};

	const onFilterInStatus = (payload: Options | null): void => {
		setSelectedStatus(payload);

		handleFilter(payload?.value, showOtherExpert);
	};

	const onShowOtherExpert = (): void => {
		setShowOtherExpert(prevState => !prevState);

		handleFilter(selectedStatus?.value, !showOtherExpert);
	};

	const onChangeSort = (sort: string): void => {
		setSort(sort);

		handleFilter(selectedStatus?.value, showOtherExpert, [sort]);
	};

	const getCompetency = (competency: string): string => {
		const list = competency?.split(',').map(compId => competenciesMap?.[compId]);

		return list?.length > 1 ? `${list[0]} +${list?.length - 1}` : list[0]!;
	};

	useEffect(() => {
		if (competenciesList?.length) {
			const map = {} as CompetenciesMapType;

			competenciesList.forEach(competency => {
				map[competency.id!] = competency.name!;
			});

			setCompetenciesMap(map);
		}
	}, [competenciesList]);

	useEffect(() => {
		if (competenciesList?.length) {
			setCompetencies([...competenciesList.map(i => ({ value: i.id!, label: i.name! }))]);
		}
	}, [competenciesList]);

	return (
		<div className="missions-table">
			<div className="missions-filters">
				<div className="missions-filters-top">
					<label className="check">
						<span className="label">Diğer koçların verdiği görevleri göster</span>
						<label className="switch">
							<input
								type="checkbox"
								checked={showOtherExpert}
								onChange={onShowOtherExpert}
							/>
							<span className="slider" />
						</label>
					</label>
					{!isMobile && (
						<button
							className="primary-btn"
							type="button"
							onClick={onCreateNewMission}>
							<i className="icons-plus" />
							Yeni Görev Oluştur
						</button>
					)}
				</div>
				<div className="missions-filters-bottom">
					{/*<Select*/}
					{/*	defaultValue={selectedCompetencies}*/}
					{/*	isMulti={false}*/}
					{/*	placeholder="Yetkinlik Seçiniz"*/}
					{/*	options={competencies}*/}
					{/*	className="single-select"*/}
					{/*	classNamePrefix="select"*/}
					{/*	onChange={payload => setSelectedCompetencies(payload)}*/}
					{/*/>*/}
					<Select
						defaultValue={selectedStatus}
						isMulti={false}
						placeholder="Görev Durumu"
						options={MissionStatusList}
						className="single-select"
						classNamePrefix="select"
						isSearchable={false}
						onChange={payload => onFilterInStatus(payload)}
					/>
				</div>
			</div>
			{isMobile ? (
				<div className="mission-card-wrapper">
					{list?.map((mission, index) => (
						<div
							key={index}
							className="mission-card">
							<div className="mission-card-header">
								<span className="mission-name">{mission?.missionName}</span>
								<span className="mission-type">
									<span className="bold">Yetkinlik Tipi:&nbsp;</span>
									{getCompetency(mission.competency!)}
								</span>
								<div className="mission-status">
									<span
										className={clsx('badge', {
											progress: mission.status === 'IN_PROGRESS',
											idle: mission.status === 'TODO',
											done: mission.status === 'DONE'
										})}>
										<Choose>
											<When condition={mission.status === 'DONE'}>
												<span className="badge-icon">
													<i className="icons-pure-check" />
												</span>
												Tamamlandı
											</When>
											<When condition={mission.status === 'IN_PROGRESS'}>
												<span className="badge-icon">
													<i className="icons-hourglass-line" />
												</span>
												Devam Ediyor
											</When>
											<Otherwise>
												<span className="badge-icon">
													<span className="badge-icon">!</span>
												</span>
												Başlanmadı
											</Otherwise>
										</Choose>
									</span>
									<span className="mission-completed-date">
										<i
											className={clsx('icons-calendar', {
												progress: mission.status === 'IN_PROGRESS',
												idle: mission.status === 'TODO',
												done: mission.status === 'DONE'
											})}
										/>
										{mission?.completedDate ? dayjs(mission?.completedDate).format('DD.MM.YYYY') : '-'}
									</span>
								</div>
								<div className="mission-due">Son Teslim Tarihi:&nbsp;{dayjs(mission?.dueDate).format('DD.MM.YYYY')}</div>
								<a
									className="white-btn"
									onClick={() => onDetail?.(mission.studentMissionId!)}>
									Görev Detayını Gör
								</a>
							</div>
						</div>
					))}
					<If condition={totalPages! > 1}>
						<Pagination
							currentPage={currentPage! + 1}
							totalPages={totalPages!}
							onPageChange={onPageChange!}
						/>
					</If>
					<div className="button-wrapper">
						<a
							className="primary-btn"
							onClick={onCreateNewMission}>
							<i className="icons-plus" />
							Yeni Görev Oluştur
						</a>
					</div>
				</div>
			) : (
				<div className="table">
					<div className="table-head">
						<div className="th no">No</div>
						<div className="th name">Görev Adı</div>
						<div className="th type">Yetkinlik Tipi</div>
						<div
							className="th due sortable"
							onClick={() => onChangeSort(sort === 'dueDate,DESC' ? 'dueDate,ASC' : 'dueDate,DESC')}>
							Son Teslim Tarihi
							{sort === 'dueDate,DESC' ? <i className="icons-alt-arrow-up" /> : <i className="icons-alt-arrow-down" />}
						</div>
						<div
							className="th status sortable"
							onClick={() => onChangeSort(sort === 'status,DESC' ? 'status,ASC' : 'status,DESC')}>
							Görev Durumu
							{sort === 'status,DESC' ? <i className="icons-alt-arrow-up" /> : <i className="icons-alt-arrow-down" />}
						</div>
						<div
							className="th done sortable"
							onClick={() => onChangeSort(sort === 'completedDate,DESC' ? 'completedDate,ASC' : 'completedDate,DESC')}>
							Tamamlanma Tarihi
							{sort === 'completedDate,DESC' ? <i className="icons-alt-arrow-up" /> : <i className="icons-alt-arrow-down" />}
						</div>
					</div>
					<div className="table-body">
						<If condition={!!list?.length}>
							{list?.map((mission, index) => (
								<div
									key={index}
									className="row"
									onClick={() => onDetail?.(mission.studentMissionId!)}>
									<div className="cell no">
										<span className="no-value">{currentPage! * perPageSize! + index + 1}</span>
									</div>
									<div className="cell name">{mission?.missionName}</div>
									<div className="cell type">{getCompetency(mission.competency!)}</div>
									<div className="cell due">{dayjs(mission?.dueDate).format('DD.MM.YYYY')}</div>
									<div className="cell status">
										<span
											className={clsx('badge', {
												progress: mission.status === 'IN_PROGRESS',
												idle: mission.status === 'TODO',
												done: mission.status === 'DONE'
											})}>
											{mission.status === 'DONE' ? (
												<>
													<span className="badge-icon">
														<i className="icons-pure-check" />
													</span>
													Tamamlandı
												</>
											) : mission.status === 'IN_PROGRESS' ? (
												<>
													<span className="badge-icon">
														<i className="icons-hourglass-line" />
													</span>
													Devam Ediyor
												</>
											) : (
												<>
													<span className="badge-icon">
														<span className="badge-icon">!</span>
													</span>
													Başlanmadı
												</>
											)}
										</span>
									</div>
									<div className="cell done">{mission?.completedDate ? dayjs(mission?.completedDate).format('DD.MM.YYYY') : '-'}</div>
								</div>
							))}
						</If>
					</div>
					<div className="table-footer">
						<If condition={totalPages! > 1}>
							<Pagination
								currentPage={currentPage! + 1}
								totalPages={totalPages!}
								onPageChange={onPageChange!}
							/>
						</If>
					</div>
				</div>
			)}
		</div>
	);
};
